import { CometChat } from '@cometchat/chat-sdk-javascript';
import {
  Box,
  CardMedia,
  Container,
  Grid,
  Link,
  Tab,
  Tabs,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tooltipClasses,
} from '@mui/material';
import chat from 'assets/images/chat.svg';
import history from 'assets/images/history.svg';
import online from 'assets/images/online.svg';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { ROUTE } from 'route/RouteEnums';
import { validateSession } from 'services/validateSession';
import { useAppDispatch } from 'store/hooks';
import { getAppointmentList } from 'store/slices/adminSlice';
import { getConversationGroupDetails, joinConversation } from 'store/slices/secureMessageSlice';
import COLORS from 'theme/colors';
import { AuthContext } from 'utils/AuthContext';
import CustomDataGrid from 'utils/customDataGrid';
import { formatDateTime,  formatPhoneNumber } from 'utils/dataFormatter';
import {
  AppointmentStatusFilter,
  DateTimeFilterOperators,
  LocationFilter,
  ProviderFilter,
} from 'utils/gridOperators';
import PopupMenu from 'utils/PopupMenu';
import { getDefaultLandingPage } from 'utils/navigationUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

function AdminDashboard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [groupDetails, setGroupDetails] = useState<any>();
  const [open, setOpen] = useState<any>();
  const [selectedTab, setSelectedTab] = useState(0);

  const { user, setUserDetails } = useContext(AuthContext);
  const pageKey = 'patient-dashboard';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(true);

  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const handleTooltipClose = () => {
    setOpen(null);
  };

  const handleMenuOpen = async (event: any, row: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveRowId(row.patientId);
    setLoading(true);
    // 🔵 Load your API data here
    await handleTooltipOpen(row.patientId);
    setLoading(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveRowId(null);
  };

  const StyledTooltip = styled(({ className, color, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltipArrow}`]: {
      backgroundColor: '#fff!important',
      boxShadow: theme.shadows[1],
    },
    [`& .${tooltipClasses.arrow}`]: {
      '&:before': {
        border: '1px solid #fff',
        color: '#fff',
      },
    },
    [`& .${tooltipClasses.tooltip}`]: {
      border: '1px solid #fff',
      padding: '12px',
      borderRadius: '8px',
    },
  }));
  const handleHistory = (e: any, id: any) => {
    e.stopPropagation();
    navigate(`${ROUTE.ADMIN_APPOINTMENT_DETAILS}/${id}`);
  };

  const handleTooltipOpen = async (id: number) => {
    try {
      const response: any = await dispatch(getConversationGroupDetails(id)).unwrap();
      if (response?.status === 200) {
        setGroupDetails(response.data);
        setOpen(id);
      }
    } catch (error) {
      console.error('Failed to fetch group details:', error);
    }
  };

  const handleJoinConversation = (g: any, e?: any) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    dispatch(joinConversation(g.groupId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          navigate(`${ROUTE.ADMIN_MESSAGES}`);
        }
      });
  };

  const getToolTipTitle = (params: any) => {
    const status = params.row.migrationStatus !== null ? params.row.migrationStatus : '';
    return <>{params.row.patientName + ' ' + status}</>;
  };

  const columns = [
    {
      field: 'action',
      headerName: 'Action',
      filterable: false,
      sortable: false,
      hideable: false,
      disableExport: true,
      renderCell: (params: any) => {
        return (
          <>
            <CardMedia
              src={chat}
              component='img'
              sx={{ width: 24, cursor: 'pointer' }}
              onClick={(e) => handleMenuOpen(e, params.row)}
            />
            <Link onClick={(e: any) => handleHistory(e, params?.row?.patientId)}>
              <CardMedia
                src={history}
                component='img'
                sx={{ width: '24px', height: 'auto', ml: '12px' }}
              />
            </Link>
          </>
        );
      },
    },
    {
      field: 'patientName',
      headerName: 'Patient Name',
      width: 200,
      hideable: false,
      renderCell: (params: any) => {
        return (
          <>
            <span>
              <Tooltip
                title={getToolTipTitle(params)}
                arrow
                placement='bottom-start'
                enterTouchDelay={0}
              >
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#101828',
                    fontFamily: 'Graphik-Regular, sans-serif',
                  }}
                >
                  {params.row.patientName}
                  <span
                    style={{
                      color: '#1570EF',
                      marginLeft: '5px',
                      fontSize: '11px',
                      fontWeight: 700,
                      lineHeight: '24px',
                    }}
                  >
                    {params.row.migrationStatus}
                  </span>
                </span>
              </Tooltip>
            </span>

            {params.row.migrationStatus === null && params.row.type === 'NEW' ? (
              <span
                style={{
                  color: '#1570EF',
                  marginLeft: '5px',
                  fontSize: '11px',
                  fontWeight: 700,
                  lineHeight: '24px',
                }}
              >
                New
              </span>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      field: 'contactNumber',
      headerName: 'Contact Number',
      width: 150,
      type: 'phone',
      disableReorder: false,
      sortable: true,
      editable: true,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
      valueGetter: (params: any) => params?.row?.contactNumber,
      renderCell: (params: any) => (
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: '#101828',
            fontFamily: 'Graphik-Regular, sans-serif',
          }}
        >
          {formatPhoneNumber(params?.row?.contactNumber)}
        </Typography>
      ),
    },
    {
      disableReorder: false,
      sortable: true,
      filterable: true,
      field: 'email',
      headerName: 'Email Address',
      width: 300,
    },
   
    {
      sortable: true,
      field: 'upcomingAppointmentDate',
      headerName: 'Appointment Date',
      description: 'Appointment Date',
      width: 170,
      type: 'dateTime',
      filterOperators: DateTimeFilterOperators,
      valueFormatter: (params: any) => formatDateTime(params?.value),
      valueGetter: (params: any) => params.row.upcomingAppointmentDate,
      renderCell: (params: any) => {
        return params?.row?.upcomingAppointmentDate ? (
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#101828',
              fontFamily: 'Graphik-Regular, sans-serif',
            }}
          >
            {dayjs.utc(params.row.upcomingAppointmentDate).local().format('MM/DD/YYYY')}
            {' - '}
            {dayjs.utc(params.row.upcomingAppointmentDate).local().format('hh:mm A')}
          </Typography>
        ) : (
          'None Scheduled'
        );
      },
    },
    {
      field: 'appointmentStatus',
      headerName: 'Appointment Status',
      description: 'Appointment Status',
      width: 172,
      filterOperators: AppointmentStatusFilter,
      valueFormatter: (params: any) => params?.value,
      valueGetter: (params: any) => params?.row?.appointmentStatus,
      renderCell: (params: any) => {
        return (
          <StyledTooltip
            arrow
            enterTouchDelay={0}
            enterDelay={0}
            title={
              <>
                {(params?.row?.appointmentStatus === null ||
                    params?.row?.appointmentStatus === '') &&
                  params?.row?.lastAppointmentDate !== null ? (
                    <Box>Status not available</Box>
                  ) : (
                    <React.Fragment>
                      <Typography
                        sx={{
                          fontSize: '18px',
                          color: '#344054',
                          fontFamily: 'Inter',
                          fontWeight: 700,
                          lineHeight: '18px',
                          marginBottom: '8px',
                        }}
                      >
                        Latest Appointment
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#667085',
                          fontWeight: 500,
                          fontFamily: 'Inter',
                          lineHeight: '18px',
                          marginTop: '10px',
                        }}
                      >
                        Provider Name
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#344054',
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          lineHeight: '18px',
                          marginBottom: '8px',
                          textTransform: 'capitalize',
                        }}
                      >
                        {params?.row?.providerName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#667085',
                          fontWeight: 500,
                          fontFamily: 'Inter',
                          lineHeight: '18px',
                          marginTop: '10px',
                        }}
                      >
                        Location
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#344054',
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          lineHeight: '18px',
                          marginBottom: '8px',
                        }}
                      >
                        {params?.row?.officeAddress}
                      </Typography>
                    </React.Fragment>
                  )}
              </>
            }
            placement='right-end'
            sx={{ padding: '12px' }}
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [20, -60],
                  },
                },
              ],
            }}
          >
            <Typography
              style={{
                textDecoration:
                    !params?.row?.appointmentStatus &&
                    params?.row?.lastAppointmentDate
                      ? ''
                      : 'underline'
              }}
              color={getAppointmentStatusColor(params?.row?.appointmentStatus)}
            >
              {params?.row?.appointmentStatus ??
              (params?.row?.lastAppointmentDate ? '-' : '')}
            </Typography>
          </StyledTooltip>
        );
      },
    },

    {
      field: 'providerName',
      headerName: 'Provider',
      width: 130,
      filterOperators: ProviderFilter,
    },

    {
      field: 'officeAddress',
      headerName: 'Appointment Location',
      filterOperators: LocationFilter,
      width: 300,
    },
  ];

  const finalColumns =
  selectedTab === 1
    ? [
      ...columns.slice(0, 2),
      {
        sortable: false,
        field: 'checkinDate',
        headerName: 'Check-in DateTime',
        width: 170,
        hideable: false,
        type: 'dateTime',
        filterOperators: DateTimeFilterOperators,
        valueFormatter: (params: any) => formatDateTime(params?.value),
        valueGetter: (params: any) => params.row.checkinDate,
        renderCell: (params: any) => {
          return params?.row?.checkinDate ? (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#101828',
                fontFamily: 'Graphik-Regular, sans-serif',
              }}
            >
              {dayjs.utc(params.row.checkinDate).local().format('MM/DD/YYYY')}
              {' - '}
              {dayjs.utc(params.row.checkinDate).local().format('hh:mm A')}
            </Typography>
          ) : (
            ''
          );
        },
      },
      ...columns.slice(2),
    ]
    : columns;

  const getAppointmentStatusColor = (appointmentStatus: any) => {
    if (appointmentStatus === 'Confirmed') {
      return '#1570EF';
    } else if (appointmentStatus === 'Complete') {
      return '#027A48';
    } else if (appointmentStatus === 'Cancelled') {
      return '#912018';
    } else if (appointmentStatus === 'Late Cancel') {
      return '#912018';
    } else if (appointmentStatus === 'Bumped') {
      return '#912018';
    } else if (appointmentStatus === 'No-show') {
      return '#912018';
    } else if (appointmentStatus === 'Scheduled') {
      return '#1570EF';
    }
    return '#1570EF';
  };

  const rowSelect = async (params: any) => {
    await validateSession();
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${params.row.patientId}`, '_blank');
    const data = {
      ...user,
      patientId: params.row.patientId,
    };
    setUserDetails(data);
  };

  const [appointmentsData, setAppointmentsData] = useState({
    myAppointments: [] as any[],
    checkedInAppointments: [] as any[],
    allAppointments: [] as any[],
    counts: { today: 0, checkedIn: 0, all: 0 },
  });

  const fetchAppointments = async () => {
    try {
      const today = dayjs();
      const formattedDate = today.format('YYYY-MM-DD');
      const timezone = dayjs.tz.guess();

      const data = {
        timezone: timezone,
        date: formattedDate,
      };

      await dispatch(getAppointmentList(data))
        .unwrap()
        .then((response: any) => {
          if (response?.status === 200) {
            const data = response.data;
            setAppointmentsData({
              myAppointments: data.myAppointmentsToday || [],
              checkedInAppointments: data.checkedInAppointmentsToday || [],
              allAppointments: data.allAppointmentsToday || [],
              counts: {
                today: data.myAppointmentsToday?.length || 0,
                checkedIn: data.checkedInAppointmentsToday?.length || 0,
                all: data.allAppointmentsToday?.length || 0,
              },
            });
          }
        });
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRowsForTab = () => {
    switch (selectedTab) {
    case 0:
      return appointmentsData.myAppointments;
    case 1:
      return appointmentsData.checkedInAppointments;
    case 2:
      return appointmentsData.allAppointments;
    default:
      return [];
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchAppointments();
    window.onpopstate = () => {
      navigate(getDefaultLandingPage());
    };
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  useEffect(() => {
    CometChat.addMessageListener(
      'chatMessage',
      new CometChat.MessageListener({
        onTextMessageReceived: (textMessage: CometChat.TextMessage) => {
          toast('You have new message', {
            type: 'info',
            icon: true,
          });
        },
        onMediaMessageReceived: (mediaMessage: CometChat.MediaMessage) => {
          toast('You have new message', {
            type: 'info',
            icon: true,
          });
        },
      })
    );
  }, []);

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
  };

  const defaultFilter = useMemo(() => {
    if (user?.roleType !== 'Provider') {
      return { items: [] };
    }

    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

    if (!fullName) {
      return { items: [] };
    }

    const providerExists = ProviderFilter?.some((f) => f.value === fullName);

    if (!providerExists) {
      return { items: [] }; // ✅ prevents failure
    }

    return {
      items: [
        {
          field: 'providerName', // ⚠️ ensure this matches column field exactly
          operator: fullName,
        },
      ],
    };
  }, [user, ProviderFilter]);


  const getTabColor = (tabIndex: number) =>
    selectedTab === tabIndex ? '#644EB5' : '#F2F4F7';
  const appointmentTodaysCount = getTabColor(0);
  const chekedInTabCountAndColor = getTabColor(1);
  const allPatinetTabCountAndColor = getTabColor(2);

  return (
    <Container maxWidth='lg'>
      <Grid container>
        <Grid item justifyContent={'left'}>
          <Typography
            variant='h5'
            position={'relative'}
            width={'fit-content'}
            sx={{ paddingTop: 6, textTransform: 'capitalize' }}
          >
            Patient Appointment Dashboard
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
        <Grid container sx={{ background: '#FFFBF1', justifyContent: 'space-between' }}>
          <Grid item xs={7.5} mb='10px'>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{
                background: '#F9FAFB',
                borderRadius: '23px',
                border: '1px solid #F2F4F7',
                marginTop: '10px',
                padding: '6px 14px',
                '.MuiTab-root.Mui-selected': {
                  background: '#9F84FF',
                  color: '#fff',
                  borderRadius: '23px',
                  fontWeight: 500,
                },
              }}
              TabIndicatorProps={{
                style: {
                  background: 'transparent',
                },
              }}
              centered
            >
              <Tab
                label={
                  <Grid>
                    {user?.roleType === 'Provider' ? 'My Appointments Today' : 'Appointments Today'}

                    <span
                      style={{
                        background: appointmentTodaysCount,
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {appointmentsData.counts.today}
                    </span>
                  </Grid>
                }
              />

              <Tab
                label={
                  <Grid>
                    Checked In Today
                    {/* eslint-disable-next-line max-len */}
                    <span
                      style={{
                        background: chekedInTabCountAndColor,
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {appointmentsData.counts.checkedIn}
                    </span>
                  </Grid>
                }
              />
              <Tab
                label={
                  <Grid>
                    All Patient Appointments Today
                    {/* eslint-disable-next-line max-len */}
                    <span
                      style={{
                        background: allPatinetTabCountAndColor,
                        borderRadius: '23px',
                        padding: '5px 7px',
                        marginLeft: '5px',
                      }}
                    >
                      {appointmentsData.counts.all}
                    </span>
                  </Grid>
                }
              />
            </Tabs>
          </Grid>
        </Grid>

        <CustomDataGrid
          rows={getRowsForTab()}

          columns={finalColumns}
          dashBordKey={pageKey + '_' + selectedTab}
          onRowSelect={rowSelect}
          userId={user.userId}
          defaultFilter={defaultFilter}
          setting={{ disableExport: !user?.permissions.includes('EXPORT_DATA') }}
        />
      </Grid>
      <PopupMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        loading={loading}
        items={groupDetails?.map((g: any) => ({
          label: g.groupName,
          onClick: () => handleJoinConversation(g),
          icon: g.activeConversation ? (
            <img src={online} width={10} height={10} color='green' alt="Online" />
          ) : null,
        }))}
        width={260}
      />
    </Container>
  );
}

export default AdminDashboard;
